import {
  buildRepeaterEndpoints,
  fetchCitySuggestions,
  resolveRepeaterApiBase,
} from "../datasources.ts";
import { encodeMaidenhead } from "../rsgb.ts";
import { classifyErrorKind, errorTypeName, trackEvent } from "./analytics.ts";
import { errorDetails } from "./format.ts";
import { FLOWS, OUTCOMES, recordFlow } from "./metrics.ts";
import {
  RepeaterInputError,
  createRepeaterSources,
  unmetRequirement,
} from "./repeater-sources.ts";
import {
  createCheckboxField,
  createCheckboxGroupField,
  createCityField,
  createFixedField,
  createNumberField,
  createPositionField,
  createSelectField,
} from "./query-fields.ts";
import type { UiContext } from "../types/ui-context.js";
import type { QueryField } from "./query-fields.ts";
import type { City, FieldOption } from "./query-fields.ts";
import type { FieldConfig, PreviewSummary, QueryValues, RepeaterSource } from "./repeater-sources.ts";
import { errorFields } from "../error-details.ts";

// The key every source gives its "repeaters within N km" filter. The position
// field's map preview draws that radius, so the shell has to know which field
// carries it; a source that named it something else would simply preview no
// circle, which is why this is one constant rather than a guess per source.
const RANGE_FIELD_KEY = "radius";

// How long the form must sit still before the map preview queries a directory.
// A preview repeats the real query (up to nine ~75 kB requests for RSGB, see
// FINDINGS.md) and a drag rewrites the coordinates on every pointermove, so the
// delay is set by what a wasted preview costs, not by what the user can feel.
const PREVIEW_QUERY_DEBOUNCE_MS = 600;

// One sentence for every repeater control the browser connectivity signal
// blocks, so tooltips and the modal submit button explain the same state.
const OFFLINE_BLOCKED_REASON = "Reconnect to query repeater directories.";

const FIELD_FACTORIES: Readonly<Record<string, (config: never) => QueryField>> = {
  select: createSelectField,
  fixed: createFixedField,
  checkboxGroup: createCheckboxGroupField,
  checkbox: createCheckboxField,
  number: createNumberField,
};

// A browser geolocation failure arrives as a GeolocationPositionError, which
// is a WebIDL platform object rather than an Error instance: its only readable
// field is a numeric `code` (1 = permission denied, 2 = position unavailable,
// 3 = timeout) and, on Firefox, no `message` at all, so stringifying one reads
// "[object GeolocationPositionError]". These sentences stand in for it so the
// failure says what actually happened everywhere it is surfaced. The wording
// doubles as the analytics classification: a denial reports as
// permission_denied and a stall as timeout instead of the catch-all "other".
const GEOLOCATION_FAILURE_TEXT: Readonly<Record<number, string>> = Object.freeze({
  1: "Location permission was denied by the browser.",
  2: "The browser could not determine a position.",
  3: "Getting the location timed out.",
});

// Online repeater directory imports. One modal serves every source: each
// source config (web/js/ui/repeater-sources.ts) declares its fields, and the grid is
// rebuilt from them on every open — which is also the reset policy: filters
// always come back at their per-source defaults, so the modal always opens in
// the state it documents (a once-ticked "include off-air" cannot silently
// stick forever). The position is the deliberate exception: it survives
// closes and source switches alike, because where the user is does not change
// with the directory they ask.
export function createRepeaterQuery(ctx: UiContext) {
  const { dom, state, log } = ctx;

  // przemienniki.net and RepeaterBook depend on the configured proxy base; a
  // blank base hides them. IRTS remains available through api.codeplug.org so
  // a service failure is visible when the user tries it, rather than silently
  // removing the action.
  const endpoints = buildRepeaterEndpoints(resolveRepeaterApiBase());
  const sources = createRepeaterSources(ctx, { endpoints });
  const sourceButtonTitles = new Map<string, string>();
  for (const source of sources) {
    sourceButtonTitles.set(source.key, dom[source.toolbarButton].title);
    if (!source.available) {
      dom[source.toolbarButton].hidden = true;
    }
  }

  let activeSource = sources[0];
  // Counts openModal() calls so a superseded one can bow out. Sources that
  // fetch a dictionary resolve after sources whose options are static, and the
  // directory buttons are now one click apart on the toolbar rather than two
  // clicks apart through a menu — so the slow first click would otherwise
  // rebuild the modal the second click had already opened.
  let openGeneration = 0;
  let fieldInstances: QueryField[] = [];
  let positionField: ReturnType<typeof createPositionField> | null = null;
  let cityField: ReturnType<typeof createCityField> | null = null;
  const positionState = { latitudeText: "", longitudeText: "" };
  // What the Place name box last settled on. Kept for the same reason
  // the coordinates are: it is where the user is, which does not change with
  // the directory they ask. Held here rather than in the field because the
  // field is rebuilt on every open.
  const cityState: { city: City | null } = { city: null };
  // True only while onCitySelected is writing the position it just chose.
  // setPosition() reports through the position field's onChange, which is also
  // how a geolocate, a typed digit or a map drag arrives -- and those must wipe
  // the city name, because it would no longer describe the coordinates. Without
  // this flag, choosing a city would immediately erase its own name.
  let applyingCity = false;
  // True while a query is awaiting its network round-trip. The submit handler
  // is async, so without this a second submit re-enters it while the first is
  // suspended, and both resolved queries insert their rows — every repeater
  // twice. An RSGB fan-out over 24 squares takes seconds, so the window is
  // wide enough to hit by double-clicking.
  let queryInFlight = false;
  // Browser-reported connectivity only. Request failures do not change this;
  // web/js/ui/connectivity.ts updates it from online/offline events.
  let online = true;
  // Why the form as it stands cannot be queried, or "" when it can. Held
  // beside queryInFlight because both disable the same button and either can
  // change while the other holds: re-enabling after a query must not undo the
  // gate, and filling the form in mid-query must not enable the button early.
  let submitBlockedReason = "";
  const submitIdleLabel = dom.repeaterQuerySubmitEl.textContent || "Query API";

  // Put the button in the state the two flags describe. The label says only
  // whether a query is running; a button disabled because the form is not
  // filled in still reads "Query API", because that is what it will do once it
  // is.
  function applySubmitState() {
    dom.repeaterQuerySubmitEl.disabled = queryInFlight || !online || submitBlockedReason.length > 0;
    dom.repeaterQuerySubmitEl.textContent = queryInFlight ? "Querying..." : submitIdleLabel;
    dom.repeaterQuerySubmitEl.title = queryInFlight
      ? ""
      : !online ? OFFLINE_BLOCKED_REASON : submitBlockedReason;
  }

  // Apply the browser connectivity signal to every entry point. The explicit
  // guards in the click and submit handlers remain necessary because disabled
  // controls can still be invoked programmatically.
  function setOnline(nextOnline: boolean): void {
    online = Boolean(nextOnline);
    for (const source of sources) {
      const button = dom[source.toolbarButton];
      button.disabled = !online;
      // Every source's own title was recorded when the modal was built.
      button.title = online ? sourceButtonTitles.get(source.key) ?? "" : OFFLINE_BLOCKED_REASON;
    }
    applySubmitState();
    if (isModalOpen()) {
      schedulePreview();
    }
  }

  // Mark the query busy: the flag is what actually rejects a re-entrant
  // submit (Enter in a text field submits the form too, not just the button),
  // while the disabled button and its label are how the user sees why the
  // second click did nothing.
  function setQueryBusy(busy: boolean): void {
    queryInFlight = busy;
    applySubmitState();
  }

  // Whether the active source can be queried with what is in the form. Each
  // source declares what it needs (requires, web/js/ui/repeater-sources.ts)
  // and the rule differs: RSGB fans out around a point and has nothing to do
  // without one, while the three country-filtered directories accept either a
  // country or a location and answer a query with neither by sending their
  // whole contents.
  //
  // This is what keeps an unrunnable form from becoming an error report.
  // Before it, the only thing that said a filter was needed was the sentence
  // RepeaterInputError carried -- which the user could not read until they had
  // pressed the button, and which arrived through the same funnel a directory
  // outage does, so every unfilled form filed a Sentry event for something no
  // one could fix in the code.
  function refreshSubmitState() {
    const values = fieldInstances.length > 0 ? collectValues() : {};
    submitBlockedReason = unmetRequirement(activeSource, values);
    applySubmitState();
  }

  // A city was committed in the autocomplete: its coordinates become the
  // form's position. setPosition() is the same entry point geolocation and the
  // map drag use, so the locator is recomputed and the preview recentres for
  // free -- the city field never touches those three inputs itself.
  function onCitySelected(city: City): void {
    cityState.city = city;
    if (!positionField) {
      return;
    }
    applyingCity = true;
    try {
      positionField.setPosition(city.latitude, city.longitude);
    } finally {
      applyingCity = false;
    }
    // Counted alongside repeater_geolocate and repeater_map_panned so the ways
    // of setting a position can be compared. Which source was open and nothing
    // else: the place name is a search term and the coordinates are a location,
    // and neither leaves the browser through analytics.
    trackEvent("repeater_city_selected", { repeater_source: activeSource.key });
    // The name and the coordinates are fine in the local debug panel, which is
    // the same detail the geolocate path logs.
    log.setStatus(`Location set to ${city.name}.`);
    log.logDebug(`${activeSource.actionLabel.toUpperCase()} CITY ${city.name} ${city.latitude.toFixed(6)},${city.longitude.toFixed(6)}`);
  }

  function buildFields(source: RepeaterSource, loadedOptions: Record<string, FieldOption[]> | null): void {
    dom.repeaterQueryGridEl.innerHTML = "";
    fieldInstances = [];
    positionField = null;
    cityField = null;
    // Nodes a field wants at the foot of the grid rather than at its own place
    // in the config order. Only the position field's map preview uses this: it
    // pictures the whole query, range circle included, so it belongs after the
    // range control and not above it.
    const tailNodes: Node[] = [];
    for (const config of source.fields) {
      let instance: QueryField;
      if (config.kind === "city") {
        const field = createCityField({
          ...config,
          // The field contacts nothing itself. No position hint is sent: the
          // ranking it buys is not worth putting the form's location in a query
          // string on every keystroke. fetchCitySuggestions still takes it.
          search: (query) => fetchCitySuggestions(endpoints.cities, query),
          // Not reportActionError: a failed suggestion is not a failed action
          // and must not take over the status line the real query uses.
          onError: (error) => log.logDebug(`CITY LOOKUP FAILED ${errorDetails(error)}`),
          onSelect: (city) => onCitySelected(city),
          // Erased by its own button: forget it, or the next open restores it.
          onClear: () => { cityState.city = null; },
          // Reopening the modal shows the place last chosen, in step with the
          // coordinates the position field restores beside it.
          initial: cityState,
        });
        cityField = field;
        instance = field;
      } else if (config.kind === "position") {
        const field = createPositionField({
          locatorPlaceholder: config.locatorPlaceholder,
          initial: positionState,
          onChange: (latitudeText, longitudeText) => {
            positionState.latitudeText = latitudeText;
            positionState.longitudeText = longitudeText;
            // The position moved by some route other than the city picker, so
            // whatever place name is in the box is now describing coordinates
            // that are no longer its own.
            if (!applyingCity) {
              cityState.city = null;
              cityField?.clear();
            }
            schedulePreview();
          },
          // Counted next to repeater_geolocate, so the three ways of setting a
          // position can be compared. Which source was open, never where the
          // drag landed — the coordinates stay in the form.
          onPan: () => trackEvent("repeater_map_panned", { repeater_source: activeSource.key }),
        });
        positionField = field;
        instance = field;
        // The button is recreated with the field on every open, so the
        // listener attaches here rather than in bindEvents.
        field.geolocateButton.addEventListener("click", onGeolocateClick);
      } else {
        const factory = FIELD_FACTORIES[config.kind];
        const options = config.optionsKey
          ? loadedOptions?.[config.optionsKey] || []
          : config.options;
        // Each factory reads the options of its own kind off the config.
        instance = (factory as (config: FieldConfig) => QueryField)({ ...config, options });
      }
      for (const node of instance.nodes) {
        dom.repeaterQueryGridEl.appendChild(node);
      }
      tailNodes.push(...(instance.tailNodes || []));
      fieldInstances.push(instance);
    }
    for (const node of tailNodes) {
      dom.repeaterQueryGridEl.appendChild(node);
    }
    bindRangeToPreview();
  }

  // Feed the range filter's value to the position field, which draws it as a
  // circle on its map preview and frames the map around it. This runs after
  // the build loop rather than inside it because the range field is declared
  // after the position field it has to reach — and both are rebuilt on every
  // open, so the listener is attached here rather than in bindEvents.
  function bindRangeToPreview() {
    const range = fieldInstances.find((instance) => instance.key === RANGE_FIELD_KEY);
    const field = positionField;
    if (!field || !range?.input) {
      return;
    }
    // The range input is rebuilt with the position field on every open, so
    // the field this listener feeds is the one built beside it.
    const applyRange = () => field.setRangeKm(Number(range.value()));
    range.input.addEventListener("input", applyRange);
    applyRange();
  }

  // Ask the active source what its current filters would return, and hand the
  // positions to the map. Nothing here can fail the form: a preview is a hint,
  // so a directory that refuses one leaves the map as it was and says so under
  // it, with the reason in the debug panel and nothing in the status line.
  //
  // Generation-counted like openModal, and for the same reason twice over: an
  // edit during a slow preview supersedes it, and so does switching to another
  // directory, whose answer must never be drawn under the first one's filters.
  let previewGeneration = 0;
  let previewTimer = 0;

  async function runPreview(source: RepeaterSource, values: QueryValues): Promise<void> {
    const generation = previewGeneration;
    positionField?.setMarkers(null, "loading");
    let result: PreviewSummary | null = null;
    try {
      result = await source.previewQuery(values);
    } catch (error) {
      if (generation !== previewGeneration) {
        return;
      }
      positionField?.setMarkers(null, "failed");
      // The whole error, as the city lookup logs it: a preview that fails
      // inside the XML parser is only diagnosable from the stack.
      log.logDebug(`${source.actionLabel.toUpperCase()} PREVIEW FAILED ${errorDetails(error)}`);
      return;
    }
    if (generation !== previewGeneration) {
      return;
    }
    // null is the sources' "there was nothing to ask" — a blank, zero or
    // negative radius. Reporting it as an empty answer would caption the map
    // "0 in range" and log a successful zero-result preview, when in fact no
    // directory was contacted and the form simply is not filled in yet.
    if (!result) {
      positionField?.setMarkers([], "off");
      return;
    }
    positionField?.setMarkers(result.points, "ok", {
      truncated: result.truncated === true,
      // The two ways the map and the Query API button can disagree about a
      // total: one the query inserts but the map cannot place, one the map
      // places but the radio cannot use.
      unmapped: result.unmapped || 0,
      unsupported: result.unsupported || 0,
    });
    log.logDebug(`${source.actionLabel.toUpperCase()} PREVIEW ${result.points.length} repeater(s)${result.truncated ? " (area clipped)" : ""}${result.unsupported ? `, ${result.unsupported} unsupported` : ""}${result.unmapped ? `, ${result.unmapped} unmapped` : ""}`);
  }

  // Called by every control in the form. The work is deferred, so a drag that
  // fires this on each pointermove still costs one preview.
  function schedulePreview() {
    // Ahead of every early return below: a change that cannot produce a
    // preview -- no radio loaded, a source without one -- still changes
    // whether the form can be submitted.
    refreshSubmitState();
    previewGeneration += 1;
    if (previewTimer) {
      clearTimeout(previewTimer);
    }
    if (!isModalOpen() || typeof activeSource?.previewQuery !== "function") {
      return;
    }
    // Going offline invalidates any preview already in flight and leaves a
    // readable reason in the open form instead of starting another request.
    if (!online) {
      positionField?.setMarkers([], "offline");
      return;
    }
    // The same guard the submit handler applies. Without it the map fetches,
    // plots squares and captions "23 in range" for a query that Query API
    // immediately refuses with "No channel schema loaded yet" — promising
    // results the button cannot deliver, and spending a directory request to
    // do it.
    if (!state.currentHeaders.length) {
      positionField?.setMarkers([], "blocked");
      return;
    }
    const values = collectValues();
    // No position is not a failed preview, it is a form not yet filled in: the
    // map is showing its stand-in line, and a count under it would be counting
    // nothing. A form that cannot be submitted as it stands says why under the
    // map instead of staying blank, because that is the readable half of the
    // disabled Query API button -- a title attribute is nothing on a phone.
    // Only when it cannot: a country-wide search is runnable and simply has no
    // position to preview, so it keeps the blank caption.
    if (!values.position) {
      const blocked = submitBlockedReason.length > 0;
      positionField?.setMarkers([], blocked ? "needed" : "off", { reason: submitBlockedReason });
      return;
    }
    const source = activeSource;
    previewTimer = setTimeout(() => {
      previewTimer = 0;
      runPreview(source, values);
    }, PREVIEW_QUERY_DEBOUNCE_MS);
  }

  function collectValues(): QueryValues {
    const values: Record<string, unknown> = {};
    for (const instance of fieldInstances) {
      values[instance.key] = instance.value();
    }
    // Keyed by the fields the source declared, which every source declares
    // as QueryValues describes.
    return values as QueryValues;
  }

  function setModalOpen(open: boolean): void {
    dom.repeaterQueryModalEl.classList.toggle("hidden", !open);
    if (open) {
      const focusable = fieldInstances.find((instance) => instance.focusTarget);
      focusable?.focusTarget?.focus();
      // Only now does the position field's map canvas have a width to measure:
      // buildFields() ran while the overlay was still display:none, where every
      // element is zero-sized. Same reason web/js/ui/repeater-map.ts shows its
      // modal before sizing the map inside it.
      positionField?.refreshPreview();
      // The position survives a close, so a reopened modal usually already has
      // one — and the map should show what is out there without waiting to be
      // touched.
      schedulePreview();
    } else {
      // Nothing in flight may land on a closed modal, or on the next one.
      previewGeneration += 1;
      if (previewTimer) {
        clearTimeout(previewTimer);
        previewTimer = 0;
      }
    }
  }

  function isModalOpen() {
    return !dom.repeaterQueryModalEl.classList.contains("hidden");
  }

  async function openModal(sourceKey: string): Promise<void> {
    const source = sources.find((entry) => entry.key === sourceKey);
    if (!online || !source || !source.available) {
      return;
    }
    const generation = ++openGeneration;
    let loadedOptions: Record<string, FieldOption[]> | null = null;
    if (source.loadOptions) {
      log.setStatus(`Loading ${source.label} query options...`);
      loadedOptions = await source.loadOptions();
      // Another source was asked for while this one was loading; it owns the
      // modal now, so leave it alone. The load still resolved, so a failure
      // here is still reported by the caller.
      if (generation !== openGeneration) {
        return;
      }
      // The browser can report offline while a dictionary request is pending;
      // do not open a network-backed modal after its controls were disabled.
      if (!online) {
        return;
      }
    }
    activeSource = source;
    buildFields(source, loadedOptions);
    dom.repeaterQueryTitleEl.textContent = source.title;
    setModalOpen(true);
    // Paired with repeater_import, this shows how many people open the filter
    // modal and never run a query.
    trackEvent("repeater_modal_opened", { repeater_source: source.key });
    log.setStatus(`Configure ${source.label} query.`);
  }

  async function geolocate() {
    if (!navigator.geolocation) {
      throw new Error("Geolocation API is not available in this browser.");
    }
    log.setStatus("Requesting browser geolocation...");
    const position = await new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      });
    }).catch((error) => {
      // See GEOLOCATION_FAILURE_TEXT: a GeolocationPositionError is not an
      // Error and carries only a numeric code, so pass every code we know
      // through as a real Error carrying the code's sentence. The code rides
      // along on the rewrite so the caller can tell a refusal (1-3) from
      // anything thrown later in this function; a genuine Error or a future
      // browser shape already reads fine and goes back untouched.
      const code = Number(error?.code);
      const failureText = GEOLOCATION_FAILURE_TEXT[code];
      if (failureText) {
        const refusal = new Error(failureText) as Error & {code?: number};
        refusal.code = code;
        throw refusal;
      }
      throw error;
    });
    const latitude = Number(position?.coords?.latitude);
    const longitude = Number(position?.coords?.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      throw new Error("Geolocation did not return valid coordinates.");
    }
    // Every source's form has a position field (its button is what started
    // this), but the form may have been rebuilt while the browser was asked.
    if (!positionField) {
      throw new Error("The query form has no position field to set.");
    }
    positionField.setPosition(latitude, longitude);
    const locator = encodeMaidenhead(latitude, longitude, 6);
    log.setStatus(`Location set to ${locator}.`);
    log.logDebug(`${activeSource.actionLabel.toUpperCase()} GEO ${latitude.toFixed(6)},${longitude.toFixed(6)} ${locator}`);
  }

  async function onGeolocateClick() {
    try {
      await geolocate();
      // Only that geolocation was used and whether it worked — the
      // coordinates it produced stay in the form.
      trackEvent("repeater_geolocate", { repeater_source: activeSource.key, outcome: "ok" });
    } catch (error) {
      trackEvent("repeater_geolocate", {
        repeater_source: activeSource.key,
        outcome: "failed",
        error_kind: classifyErrorKind(error),
      });
      // A refusal (codes 1-3) is the user's decision or the environment's,
      // never a defect in this app: they denied the permission prompt, the OS
      // could not produce a fix, or the request stalled. Such a failure is
      // reported like a cancelled action -- the status line and the debug
      // panel, not Sentry, which would stringify a code-only failure into a
      // title nobody could read -- while the GA event above keeps the failure
      // rate measurable. Anything else thrown from geolocate() (coordinates
      // the browser resolved that make no sense, a setPosition that failed)
      // is a genuine surprise and keeps the error funnel with its traceback.
      if (GEOLOCATION_FAILURE_TEXT[Number(errorFields(error).code)]) {
        log.reportActionCancelled(`${activeSource.actionLabel} geolocation`, String(errorFields(error).message));
        return;
      }
      log.reportActionError(`${activeSource.actionLabel} geolocation`, error);
    }
  }

  function bindEvents() {
    // One delegated listener on the grid, which outlives the fields inside it,
    // so a field kind added later is previewed without knowing about this. Both
    // event types: checkboxes and selects report on "change", text and number
    // boxes on "input".
    for (const type of ["input", "change"]) {
      dom.repeaterQueryGridEl.addEventListener(type, schedulePreview);
    }
    for (const source of sources) {
      dom[source.toolbarButton].addEventListener("click", async () => {
        if (!online) {
          return;
        }
        try {
          await openModal(source.key);
        } catch (error) {
          log.reportActionError(`${source.actionLabel} modal`, error);
        }
      });
    }
    dom.repeaterQueryCancelEl.addEventListener("click", () => {
      setModalOpen(false);
      log.setStatus(`Cancelled ${activeSource.label} query.`);
    });
    dom.repeaterQueryModalEl.addEventListener("click", (event) => {
      if (event.target === dom.repeaterQueryModalEl) {
        setModalOpen(false);
      }
    });
    dom.repeaterQueryFormEl.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (queryInFlight || !online) {
        return;
      }
      setQueryBusy(true);
      // Captured before the await, for the same reason the clone paths capture
      // the radio: Cancel and the backdrop close the modal without waiting for
      // the query, so the user can open a different directory while this one is
      // still in flight, and openModal() reassigns activeSource when they do.
      // Every line below has to keep meaning the source the query started on.
      const source = activeSource;
      try {
        if (!state.currentHeaders.length) {
          log.setStatus("No channel schema loaded yet.");
          setModalOpen(false);
          return;
        }
        await source.runQuery(collectValues());
        recordFlow(FLOWS.REPEATER_QUERY, OUTCOMES.OK, { repeater_source: source.key });
        setModalOpen(false);
      } catch (error) {
        // The only place a failed directory lookup is reported at all: the
        // repeater_import event fires on success, so until now a proxy that
        // started returning errors looked exactly like nobody running a query.
        // Every source shares this one handler, so one call site covers all of
        // them.
        //
        // Input the form itself rejected is "blocked" rather than "failed":
        // nothing was requested, there is nothing wrong with the directory, and
        // the user can fix it in the box in front of them. Counting those as
        // failures is what would have an alert firing over a missing location.
        const blocked = error instanceof RepeaterInputError;
        recordFlow(FLOWS.REPEATER_QUERY, blocked ? OUTCOMES.BLOCKED : OUTCOMES.FAILED, {
          repeater_source: source.key,
          error_kind: classifyErrorKind(error),
          error_type: errorTypeName(error),
        });
        // Input the form rejected is reported like a precondition failure --
        // the modal and the debug panel, not Sentry. The sentence it carries
        // is the instruction that clears it, and a form the user can fix is
        // not a defect to capture. The button gate above makes the missing
        // location unreachable from the UI; this covers the checks it does not
        // gate, and any a source adds later.
        if (blocked) {
          log.reportActionRejected(`${source.actionLabel} query`, error);
        } else {
          log.reportActionError(`${source.actionLabel} query`, error);
        }
      } finally {
        setQueryBusy(false);
      }
    });
  }

  return { bindEvents, isModalOpen, setModalOpen, setOnline };
}
